import apiClient from './api';

export interface Goal {
  id: string;
  title: string;
  description?: string;
  targetAmount: number;
  currentAmount: number;
  deadline: string;
  category: string;
  priority: 'low' | 'medium' | 'high';
  status: 'active' | 'completed' | 'paused';
  createdAt: string;
  updatedAt: string;
}

export const goalsService = {
  // Get all goals for the current user
  async getGoals(): Promise<Goal[]> {
    try {
      const response = await apiClient.get('/goals');

      return response.data.data || [];
    } catch (error) {
      console.warn('Failed to fetch goals from API, using mock data:', error);
      // Return mock data as fallback

      return [
        {
          id: '1',
          title: 'Emergency Fund',
          description: 'Build an emergency fund for unexpected expenses',
          targetAmount: 10000,
          currentAmount: 3500,
          deadline: '2024-12-31',
          category: 'savings',
          priority: 'high',
          status: 'active',
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-15T00:00:00Z',
        },
        {
          id: '2',
          title: 'Vacation Fund',
          description: 'Save for a summer vacation',
          targetAmount: 5000,
          currentAmount: 2200,
          deadline: '2024-06-01',
          category: 'travel',
          priority: 'medium',
          status: 'active',
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-20T00:00:00Z',
        },
        {
          id: '3',
          title: 'New Laptop',
          description: 'Save for a new MacBook Pro',
          targetAmount: 2500,
          currentAmount: 1800,
          deadline: '2024-04-15',
          category: 'electronics',
          priority: 'medium',
          status: 'active',
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-10T00:00:00Z',
        },
        {
          id: '4',
          title: 'Debt Payoff',
          description: 'Pay off credit card debt',
          targetAmount: 8000,
          currentAmount: 6000,
          deadline: '2024-08-01',
          category: 'debt',
          priority: 'high',
          status: 'active',
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-25T00:00:00Z',
        },
      ];
    }
  },

  // Create a new goal
  async createGoal(goal: Omit<Goal, 'id' | 'createdAt' | 'updatedAt'>): Promise<Goal> {
    try {
      const response = await apiClient.post('/goals', goal);

      return response.data.data;
    } catch (error) {
      console.error('Failed to create goal:', error);
      throw error;
    }
  },

  // Update an existing goal
  async updateGoal(id: string, goal: Partial<Goal>): Promise<Goal> {
    try {
      const response = await apiClient.put(`/goals/${id}`, goal);

      return response.data.data;
    } catch (error) {
      console.error('Failed to update goal:', error);
      throw error;
    }
  },

  // Update goal progress (add/subtract from current amount)
  async updateGoalProgress(id: string, amount: number): Promise<Goal> {
    try {
      const response = await apiClient.patch(`/goals/${id}/progress`, { amount });

      return response.data.data;
    } catch (error) {
      console.error('Failed to update goal progress:', error);
      throw error;
    }
  },

  // Delete a goal
  async deleteGoal(id: string): Promise<void> {
    try {
      await apiClient.delete(`/goals/${id}`);
    } catch (error) {
      console.error('Failed to delete goal:', error);
      throw error;
    }
  },

  // Get goal categories
  async getGoalCategories(): Promise<string[]> {
    try {
      const response = await apiClient.get('/goals/categories');

      return response.data.data || [];
    } catch (error) {
      console.warn('Failed to fetch goal categories from API, using defaults:', error);

      return ['savings', 'travel', 'electronics', 'home', 'education', 'health', 'debt', 'investment', 'other'];
    }
  },

  // Get goal statistics
  async getGoalStats(): Promise<{
    totalGoals: number;
    activeGoals: number;
    completedGoals: number;
    totalTargetAmount: number;
    totalCurrentAmount: number;
    overallProgress: number;
  }> {
    try {
      const response = await apiClient.get('/goals/stats');

      return response.data.data;
    } catch (error) {
      console.warn('Failed to fetch goal stats from API, calculating from mock data:', error);
      const goals = await this.getGoals();

      const activeGoals = goals.filter((g) => g.status === 'active');
      const completedGoals = goals.filter((g) => g.status === 'completed');

      const totalTargetAmount = goals.reduce((sum, g) => sum + g.targetAmount, 0);
      const totalCurrentAmount = goals.reduce((sum, g) => sum + g.currentAmount, 0);

      return {
        totalGoals: goals.length,
        activeGoals: activeGoals.length,
        completedGoals: completedGoals.length,
        totalTargetAmount,
        totalCurrentAmount,
        overallProgress: totalTargetAmount > 0 ? (totalCurrentAmount / totalTargetAmount) * 100 : 0,
      };
    }
  },
};

export default goalsService;
